import type {
  BundleManifest,
  ApproachReferenceData,
  ApproachReferenceManifest,
  EncoderManifest,
  PlannerManifest,
  PlannerTable,
  PolicyManifest,
  ReferenceData,
  ReferenceManifest,
} from "./types";

export const ASSET_ROOT = new URL("../assets/", import.meta.url);

export function assetUrl(path: string): URL {
  return new URL(path, ASSET_ROOT);
}

export async function fetchBuffer(path: string): Promise<ArrayBuffer> {
  const response = await fetch(assetUrl(path));
  if (!response.ok) throw new Error(`asset ${path}: HTTP ${response.status}`);
  return response.arrayBuffer();
}

export async function fetchText(path: string): Promise<string> {
  return new TextDecoder().decode(await fetchBuffer(path));
}

export async function fetchJson<T>(path: string): Promise<T> {
  return JSON.parse(await fetchText(path)) as T;
}

export interface DemoAssets {
  bundle: BundleManifest;
  planner: PlannerManifest;
  table: PlannerTable;
  policy: PolicyManifest;
  encoder: EncoderManifest;
  references: ReferenceData;
}

async function loadApproachReferences(): Promise<ApproachReferenceData> {
  const manifest = await fetchJson<ApproachReferenceManifest>("approach/approach.json");
  const load = (name: string) => fetchBuffer(`approach/${manifest.arrays[name]?.file ?? ""}`);
  const [jointPos, jointVel, pelvisQuat, pelvisLinVel, pelvisAngVel, contact] = await Promise.all([
    load("joint_pos"),
    load("joint_vel"),
    load("pelvis_quat"),
    load("pelvis_lin_vel"),
    load("pelvis_ang_vel"),
    load("contact"),
  ]);
  return {
    manifest,
    jointPos: new Float32Array(jointPos),
    jointVel: new Float32Array(jointVel),
    pelvisQuat: new Float32Array(pelvisQuat),
    pelvisLinVel: new Float32Array(pelvisLinVel),
    pelvisAngVel: new Float32Array(pelvisAngVel),
    contact: new Uint8Array(contact),
  };
}

async function loadReferences(withApproach: boolean): Promise<ReferenceData> {
  const manifest = await fetchJson<ReferenceManifest>("references/references.json");
  const load = (name: string) => fetchBuffer(`references/${manifest.arrays[name]?.file ?? ""}`);
  const [jointPos, jointVel, pelvisQuat, pelvisLinVel, pelvisAngVel, contact] =
    await Promise.all([
      load("joint_pos"),
      load("joint_vel"),
      load("pelvis_quat"),
      load("pelvis_lin_vel"),
      load("pelvis_ang_vel"),
      load("contact"),
    ]);
  const references: ReferenceData = {
    manifest,
    jointPos: new Float32Array(jointPos),
    jointVel: new Float32Array(jointVel),
    pelvisQuat: new Float32Array(pelvisQuat),
    pelvisLinVel: new Float32Array(pelvisLinVel),
    pelvisAngVel: new Float32Array(pelvisAngVel),
    contact: new Uint8Array(contact),
  };
  if (withApproach) references.approach = await loadApproachReferences();
  return references;
}

const PLANNER_PROFILES: Record<string, string> = {
  v7_1: "planner/planner-v7_1.json",
  v9_3_web: "planner/planner-v9_3_web.json",
};

export async function loadAssets(
  progress: (fraction: number, detail: string) => void,
  profile = "v7_1",
): Promise<DemoAssets> {
  const plannerPath = PLANNER_PROFILES[profile];
  if (!plannerPath) throw new Error(`unknown planner profile: ${profile}`);
  progress(0.02, "Reading the local bundle…");
  const [bundle, planner, table, policy, encoder] = await Promise.all([
    fetchJson<BundleManifest>("bundle.json"),
    fetchJson<PlannerManifest>(plannerPath),
    fetchJson<PlannerTable>("planner/planner-table.json"),
    fetchJson<PolicyManifest>("models/policy.manifest.json"),
    fetchJson<EncoderManifest>("models/theia-tiny.manifest.json"),
  ]);
  progress(0.08, "Loading the exact Repose references…");
  const references = await loadReferences(Boolean(planner.config.approach_enabled));
  progress(0.14, "Reference library ready");
  return { bundle, planner, table, policy, encoder, references };
}
